import React, { useState } from "react";
import { Container, Form, Button, Alert, Card, InputGroup } from "react-bootstrap";
import { FaMobileAlt, FaLock, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import "./AuthModal.css";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  /* STEP 1 → SEND OTP */
  const handleSendOtp = async () => {
    if (mobile.length !== 10) {
      setError("Enter valid 10-digit mobile number");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const res = await api.post("/users/send-otp", { mobile });

      if (res.data?.success) {
        setSuccess("OTP sent successfully 📲");
        setStep(2);
      } else {
        setError(res.data?.message || "OTP send failed");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  /* STEP 2 → VERIFY OTP */
  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      setError("Enter 6-digit OTP");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const res = await api.post("/users/verify-otp", { mobile, otp });

      if (res.data?.success) {
        setSuccess("OTP verified ✅");
        setStep(3);
      } else {
        setError(res.data?.message || "Invalid OTP");
      }
    } catch (err) {
      setError(err.response?.data?.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  /* STEP 3 → RESET PASSWORD */
  const handleResetPassword = async () => {
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const res = await api.post("/users/reset-password", {
        mobile,
        password,
      });

      if (res.data?.success) {
        setSuccess("Password reset successfully 🎉");

        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError(res.data?.message || "Reset failed");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-page bg-dark py-5" style={{ minHeight: "80vh" }}>
      <Container className="d-flex justify-content-center align-items-center">
        <Card className="reset-card border-0 shadow-lg" style={{ width: "450px", background: "#18191a", color: "#fff" }}>
          <Card.Body className="p-4">
            <div className="text-center mb-4">
              <h3 className="fw-bold text-orange">Reset Password</h3>
              <p className="text-secondary small">Follow the steps to regain access to your account</p>
            </div>

            <div className="steps-indicator d-flex justify-content-between mb-4 px-2">
              <div className={`step-dot ${step >= 1 ? "active" : ""}`}>1</div>
              <div className="step-line"></div>
              <div className={`step-dot ${step >= 2 ? "active" : ""}`}>2</div>
              <div className="step-line"></div>
              <div className={`step-dot ${step >= 3 ? "active" : ""}`}>3</div>
            </div>

            {error && (
              <Alert variant="danger" className="d-flex align-items-center gap-2 border-0 bg-danger bg-opacity-10 text-danger small py-2">
                <FaExclamationTriangle /> {error}
              </Alert>
            )}
            {success && (
              <Alert variant="success" className="d-flex align-items-center gap-2 border-0 bg-success bg-opacity-10 text-success small py-2">
                <FaCheckCircle /> {success}
              </Alert>
            )}

            {/* STEP 1 */}
            {step === 1 && (
              <Form>
                <Form.Group className="mb-4">
                  <Form.Label className="small text-secondary">Mobile Number</Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-dark border-secondary text-secondary">
                      <FaMobileAlt />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      className="bg-dark border-secondary text-white"
                      placeholder="Enter 10-digit mobile"
                      value={mobile}
                      maxLength={10}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                    />
                  </InputGroup>
                </Form.Group>

                <Button
                  className="w-100 sign-btn py-2 border-0"
                  onClick={handleSendOtp}
                  disabled={loading}
                >
                  {loading ? "Sending OTP..." : "Send Verification OTP"}
                </Button>
              </Form>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <Form>
                <Form.Group className="mb-4">
                  <Form.Label className="small text-secondary">Verification Code</Form.Label>
                  <Form.Control
                    type="text"
                    className="bg-dark border-secondary text-white text-center fs-4 fw-bold letter-spacing-4"
                    placeholder="------"
                    value={otp}
                    maxLength={6}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  />
                  <Form.Text className="text-secondary text-center d-block mt-2">
                    Enter the code sent to +91 {mobile}
                  </Form.Text>
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    className="flex-grow-1"
                    onClick={() => setStep(1)}
                  >
                    Back
                  </Button>
                  <Button
                    className="sign-btn flex-grow-2 w-100 border-0"
                    onClick={handleVerifyOtp}
                    disabled={loading}
                  >
                    {loading ? "Verifying..." : "Verify OTP"}
                  </Button>
                </div>
              </Form>
            )}

            {/* STEP 3 */}
            {step === 3 && (
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label className="small text-secondary">New Password</Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-dark border-secondary text-secondary">
                      <FaLock />
                    </InputGroup.Text>
                    <Form.Control
                      type="password"
                      className="bg-dark border-secondary text-white"
                      placeholder="Min 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="small text-secondary">Confirm New Password</Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-dark border-secondary text-secondary">
                      <FaLock />
                    </InputGroup.Text>
                    <Form.Control
                      type="password"
                      className="bg-dark border-secondary text-white"
                      placeholder="Repeat password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </InputGroup>
                </Form.Group>

                <Button
                  className="w-100 sign-btn py-2 border-0"
                  onClick={handleResetPassword}
                  disabled={loading}
                >
                  {loading ? "Updating..." : "Update Password"}
                </Button>
              </Form>
            )}

            <div className="text-center mt-4 pt-2 border-top border-secondary">
              <a href="/login" className="text-secondary text-decoration-none small" onClick={(e) => { e.preventDefault(); navigate("/login"); }}>
                Return to Login
              </a>
            </div>
          </Card.Body>
        </Card>
      </Container>

      <style>{`
        .steps-indicator { position: relative; }
        .step-dot { 
          width: 30px; height: 30px; border-radius: 50%; 
          background: #242526; display: flex; align-items: center; 
          justify-content: center; font-weight: bold; font-size: 14px;
          border: 2px solid #3a3b3c; z-index: 2;
        }
        .step-dot.active { background: #ff9900; color: #000; border-color: #ff9900; }
        .step-line { 
          flex-grow: 1; height: 2px; background: #3a3b3c; 
          align-self: center; margin: 0 -5px; z-index: 1;
        }
        .letter-spacing-4 { letter-spacing: 0.5rem; }
      `}</style>
    </div>
  );
};

export default ResetPassword;
